import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Utils for drawing the circle
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const mapNumber = (number, in_min, in_max, out_min, out_max) => {
  return (number - in_min) * (out_max - out_min) / (in_max - in_min) + out_min;
};

// SVG Circle component
const SVGCircle = ({ radius, stroke = "var(--primary-color)" }) => {
  const size = 64; // or calculate based on rem
  const center = size / 2;
  const r = center - 2; // small padding

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0
      }}
    >
      <path
        fill="none"
        stroke={stroke}
        strokeWidth="4"
        d={describeArc(center, center, r, 0, radius)}
      />
    </svg>
  );
};


// Main Countdown component
const Countdown = ({ countdownDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0
  });

  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      const then = moment(new Date(countdownDate));
      const now = moment();
      const diff = then.diff(now);
  
      if (diff <= 0) {
        clearInterval(interval);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
  
      const duration = moment.duration(diff);
  
      const days = Math.floor(duration.asDays());
      const hours = duration.hours();
      const minutes = duration.minutes();
      const seconds = duration.seconds();
  
      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);
  
    return () => clearInterval(interval);
  }, [countdownDate]);

  const { days, hours, minutes, seconds } = timeLeft;
  // The day ring has no natural full-scale (unlike hours/minutes/seconds), so
  // we bucket it. Guarantee the scale is at least `days` — an event a year out
  // (e.g. 344 days) used to exceed the 150 cap, making mapNumber() go negative
  // and render the arc inverted/broken. Growing the scale keeps it in range.
  const dayScale = Math.max(days, days > 100 ? 150 : days > 30 ? 100 : 30);
  const dayStroke = "var(--primary-color)";

  // Clamp any ring sweep to a drawable [0, 360) range: describeArc() can't draw
  // a degenerate full 360° circle, and out-of-scale inputs must not go negative.
  const arc = (value, scale) =>
    Math.max(0, Math.min(359.999, mapNumber(value, scale, 0, 0, 360)));

  const wrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap'
  };

  const itemStyle = {
    color: '#111',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    lineHeight: '1.2rem',
    margin: '0.25rem',
    paddingTop: '0.25rem',
    position: 'relative',
    width: '4rem',
    height: '4rem'
  };

  const labelStyle = {
    color: '#333',
    fontSize: '0.5rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    marginTop: '0.1rem'
  };

  return (
    <div style={wrapperStyle}>
        <div style={itemStyle}>
          <SVGCircle radius={arc(days, dayScale)} stroke={dayStroke} />
            {days}
            <span style={labelStyle}>{t('days')}</span>
        </div>
        <div style={itemStyle}>
            <SVGCircle radius={arc(hours, 24)} />
            {hours}
            <span style={labelStyle}>{t('hours')}</span>
        </div>
        <div style={itemStyle}>
            <SVGCircle radius={arc(minutes, 60)} />
            {minutes}
            <span style={labelStyle}>{t('minutes')}</span>
        </div>
        <div style={itemStyle}>
            <SVGCircle radius={arc(seconds, 60)} />
            {seconds}
            <span style={labelStyle}>{t('seconds')}</span>
        </div>
    </div>

  );
};

export default Countdown;
